import { type FC, memo, useEffect, useMemo } from 'react';
import SegmentedControl from 'SegmentedControl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CurrentlyStudying } from 'CurrentlyStudying';
import { Card } from 'Card';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useEffectOnce } from 'react-use';
import { useNextUpStream } from 'NextUp/hooks/useNextUpStream';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import './locales/studentDashboard/studentDashboard-en.json';
import { useLaunchablePlaylists, usePlaylist } from 'Playlists';
import { getConcentrationPlaylistPackIds } from 'Cohorts';
import { useCurrentUser } from 'FrontRoyalAngular';
import { getActivePlaylistLocalePackId, getCohort } from 'Users';
import { useProjectsForPlaylist } from 'LearnerProjects';
import { isValidDashboardTab } from './typeGuards';
import { Curriculum } from './Curriculum';

type Props = {
    card?: boolean;
    segmentedControlClassNames?: {
        base?: string;
        tabList?: string;
    };
};

function useTabs(hideCurrentlyStudying: boolean) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentDashboard.studentDashboard' });
    const tabs = useMemo(() => {
        const _tabs = [
            {
                title: t('currentlyStudying'),
                key: 'currently-studying',
                content: <CurrentlyStudying />,
            },
            {
                title: t('curriculum'),
                key: 'curriculum',
                content: <Curriculum />,
            },
        ] as const;

        if (hideCurrentlyStudying) {
            return _tabs.filter(tab => tab.key !== 'currently-studying');
        }

        return _tabs;
    }, [t, hideCurrentlyStudying]);
    return tabs;
}

const StudyBoxComponent: FC<Props> = ({ card = true, segmentedControlClassNames = {} }) => {
    const { studyTab } = useParams();
    const navigate = useNavigate();
    const AppHeaderViewModel = useAppHeaderViewModel();
    const { nextStreamInPlaylist } = useNextUpStream();
    const currentUser = useCurrentUser();
    const cohort = getCohort(currentUser);
    const concentrationPlaylistPackIds = getConcentrationPlaylistPackIds(cohort);
    const launchableConcentrationPlaylists = useLaunchablePlaylists(concentrationPlaylistPackIds);
    const noUnlockedConcentrationPlaylists = useMemo(
        () => launchableConcentrationPlaylists.length === 0 && concentrationPlaylistPackIds.length > 0,
        [launchableConcentrationPlaylists, concentrationPlaylistPackIds],
    );
    const activePlaylistLocalePackId = useMemo(() => getActivePlaylistLocalePackId(currentUser), [currentUser]);
    const activePlaylist = usePlaylist(activePlaylistLocalePackId ?? '');
    const activePlaylistProjects = useProjectsForPlaylist(activePlaylistLocalePackId ?? '');
    const [searchParams, _setSearchParams] = useSearchParams();

    const tabs = useTabs(noUnlockedConcentrationPlaylists || !activePlaylist);

    useEffect(() => {
        if (tabs.length === 1 && isValidDashboardTab(studyTab) && studyTab !== tabs[0].key) {
            navigate(`/dashboard/${tabs[0].key}`);
        }
    }, [tabs, studyTab, navigate]);

    useEffectOnce(() => {
        // This should be safe to do since we default to the currently studying tab
        // in StudentDashboardRoutes.tsx
        const hasProjects = activePlaylistProjects.length > 0;
        if (!nextStreamInPlaylist && !hasProjects && !searchParams.get('projectRequirementIdentifier')) {
            // Default to the curriculum tab if there is no next stream or projects due to completion or being locked
            navigate('/dashboard/curriculum');
        }
    });

    useEffect(
        () => () => {
            AppHeaderViewModel.learningBoxMode = null;
        },
        [AppHeaderViewModel],
    );

    const segmentedControl = useMemo(
        () => (
            <SegmentedControl
                classNames={{
                    base: twMerge('mt-5', segmentedControlClassNames.base),
                    tabList: segmentedControlClassNames.tabList,
                }}
                segments={tabs}
                selectedSegment={isValidDashboardTab(studyTab) ? studyTab : tabs[0].key}
                onSegmentChange={key => {
                    // Don't trigger navigation if the current tab is already selected
                    if (key === studyTab) return;

                    navigate(`/dashboard/${key}`);
                }}
                // don't destroy the inactive tab panels here because swapping between the Currently Studying and Curriculum tabs
                // has a noticeable delay, which we think is unexpected. ticket for investigating the delay: https://trello.com/c/Px8aNSfj
                destroyInactiveTabPanel={false}
            />
        ),
        [tabs, studyTab, navigate, segmentedControlClassNames],
    );

    return card ? <Card padding={0}>{segmentedControl}</Card> : segmentedControl;
};

export const StudyBox = memo(StudyBoxComponent) as typeof StudyBoxComponent;
